/* ==========================================================================
   VYLNS — Product page behaviour
   Loaded only on the product template, after vylns.js.
   Handles: gallery sync, variant selection, inventory messaging,
   size-guide / fit-finder drawers, and the complete-the-look upsell.
   ========================================================================== */
(function () {
  'use strict';

  function money(cents, format) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      }).format(cents / 100);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }

  /* --- Gallery ----------------------------------------------------------
     Thumbs and the swipeable stage stay in sync in both directions. On
     mobile the thumb rail is hidden and the dots take over. */
  function initGallery(root) {
    (root || document).querySelectorAll('[data-v-gallery]').forEach(function (gal) {
      if (gal._bound) return;
      gal._bound = true;

      var slides = gal.querySelector('[data-v-gal-slides]');
      var thumbs = Array.prototype.slice.call(gal.querySelectorAll('[data-v-gal-thumb]'));
      var dots = Array.prototype.slice.call(gal.querySelectorAll('[data-v-gal-dot]'));

      function activate(i) {
        thumbs.forEach(function (t, n) { t.classList.toggle('is-on', n === i); });
        dots.forEach(function (d, n) { d.classList.toggle('is-on', n === i); });
      }

      function goTo(i) {
        var slide = slides.children[i];
        if (!slide) return;
        slides.scrollTo({ left: slide.offsetLeft - slides.offsetLeft, behavior: 'smooth' });
        activate(i);
      }

      thumbs.forEach(function (t, i) { t.addEventListener('click', function () { goTo(i); }); });
      dots.forEach(function (d, i) { d.addEventListener('click', function () { goTo(i); }); });

      // Keep indicators honest when the user swipes instead of tapping.
      var timer;
      slides.addEventListener('scroll', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          var i = Math.round(slides.scrollLeft / slides.clientWidth);
          activate(i);
        }, 90);
      }, { passive: true });

      activate(0);
      gal._goTo = goTo;
    });
  }

  /* --- Variant selection ------------------------------------------------
     Reads the variant array printed into the page as JSON rather than
     re-fetching, so switching a size is instant. Updates URL, price,
     inventory copy, the sticky bar and the buy button in one pass. */
  function initVariants(root) {
    (root || document).querySelectorAll('[data-v-product]').forEach(function (form) {
      if (form._bound) return;
      form._bound = true;

      var dataEl = form.querySelector('[data-v-variants]');
      if (!dataEl) return;
      var variants = JSON.parse(dataEl.textContent);
      var idInput = form.querySelector('[data-v-variant-id]');
      var priceEl = form.querySelector('[data-v-price]');
      var compareEl = form.querySelector('[data-v-compare]');
      var addBtn = form.querySelector('[data-v-add]');
      var invEl = form.querySelector('[data-v-inventory]');
      var lowAt = parseInt(form.dataset.vLowStock, 10) || 12;

      function currentOptions() {
        return Array.prototype.slice
          .call(form.querySelectorAll('[data-v-option]:checked'))
          .map(function (i) { return i.value; });
      }

      function match(opts) {
        return variants.find(function (v) {
          return v.options.every(function (o, i) { return o === opts[i]; });
        });
      }

      /* Grey out combinations that don't exist rather than letting someone
         pick a dead end and hit an error at add-to-cart. */
      function refreshAvailability() {
        var opts = currentOptions();
        form.querySelectorAll('[data-v-option]').forEach(function (input) {
          var idx = parseInt(input.dataset.vOptionIndex, 10);
          var test = opts.slice();
          test[idx] = input.value;
          var v = match(test);
          input.disabled = !v || !v.available;
        });
      }

      function update() {
        var v = match(currentOptions());
        refreshAvailability();

        if (!v) {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
          return;
        }

        idInput.value = v.id;

        if (priceEl) priceEl.textContent = money(v.price);
        if (compareEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareEl.textContent = money(v.compare_at_price);
            compareEl.hidden = false;
          } else { compareEl.hidden = true; }
        }

        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? 'Add to cart — ' + money(v.price) : 'Sold out';

        if (invEl) {
          var qty = v.inventory_quantity;
          if (!v.available) {
            invEl.dataset.level = 'out';
            invEl.querySelector('span').textContent = 'Sold out in this size';
          } else if (v.inventory_management && qty > 0 && qty <= lowAt) {
            invEl.dataset.level = 'low';
            invEl.querySelector('span').textContent = 'Only ' + qty + ' left';
          } else {
            invEl.dataset.level = 'ok';
            invEl.querySelector('span').textContent = 'In stock — ships in 1–2 business days';
          }
        }

        // Keep the address bar shareable without a reload.
        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState({}, '', url.toString());
        }

        // Jump the gallery to this variant's image if it has one.
        var gal = document.querySelector('[data-v-gallery]');
        if (gal && gal._goTo && v.featured_media_position != null) {
          gal._goTo(v.featured_media_position);
        }

        // Mirror into the sticky bar.
        var sticky = document.querySelector('[data-v-sticky-atc]');
        if (sticky) {
          var sid = sticky.querySelector('input[name="id"]');
          var sbtn = sticky.querySelector('button[name="add"]');
          var sp = sticky.querySelector('.v-qa__price');
          if (sid) sid.value = v.id;
          if (sp) sp.textContent = money(v.price);
          if (sbtn) {
            sbtn.disabled = !v.available;
            sbtn.textContent = v.available ? 'Add to cart — ' + money(v.price) : 'Sold out';
          }
        }

        // Highlight the matching row in the size chart.
        var size = currentOptions()[parseInt(form.dataset.vSizeIndex, 10)] || '';
        document.querySelectorAll('[data-v-size-row]').forEach(function (row) {
          row.classList.toggle('is-match', row.dataset.vSizeRow === size);
        });
      }

      form.querySelectorAll('[data-v-option]').forEach(function (i) {
        i.addEventListener('change', update);
      });
      update();
    });
  }

  /* --- Drawers (size guide + fit finder) -------------------------------- */
  function initDrawers(root) {
    (root || document).querySelectorAll('[data-v-drawer-open]').forEach(function (btn) {
      if (btn._bound) return;
      btn._bound = true;
      btn.addEventListener('click', function () {
        var d = document.getElementById(btn.dataset.vDrawerOpen);
        if (!d) return;
        d.classList.add('is-open');
        d.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        var close = d.querySelector('[data-v-drawer-close]');
        if (close) close.focus();
      });
    });

    (root || document).querySelectorAll('.v-dr').forEach(function (d) {
      if (d._bound) return;
      d._bound = true;
      function close() {
        d.classList.remove('is-open');
        d.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
      d.querySelectorAll('[data-v-drawer-close]').forEach(function (b) {
        b.addEventListener('click', close);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && d.classList.contains('is-open')) close();
      });
    });
  }

  /* --- Fit finder -------------------------------------------------------
     Deliberately simple and honest: height and weight against the size
     bands the merchant sets in the section schema, plus a fit preference
     that nudges one size either way. It reports the band it matched so the
     shopper can sanity-check the recommendation rather than just trust it. */
  function initFitFinder(root) {
    var ff = (root || document).querySelector('[data-v-fit-finder]');
    if (!ff || ff._bound) return;
    ff._bound = true;

    var bands = JSON.parse(ff.querySelector('[data-v-fit-bands]').textContent);
    var out = ff.querySelector('[data-v-fit-out]');
    var sizeEl = ff.querySelector('[data-v-fit-size]');
    var noteEl = ff.querySelector('[data-v-fit-note]');

    ff.querySelector('[data-v-fit-submit]').addEventListener('click', function () {
      var h = parseFloat(ff.querySelector('[name="height"]').value);
      var w = parseFloat(ff.querySelector('[name="weight"]').value);
      var pref = ff.querySelector('[name="fit"]').value;
      if (!h || !w) return;

      var hit = bands.find(function (b) {
        return w >= b.weight_min && w <= b.weight_max && h >= b.height_min && h <= b.height_max;
      }) || bands.find(function (b) { return w >= b.weight_min && w <= b.weight_max; });

      if (!hit) {
        sizeEl.textContent = '—';
        noteEl.textContent = 'Outside our charted range — contact us and we will size you personally';
        out.classList.add('is-on');
        return;
      }

      var order = bands.map(function (b) { return b.size; });
      var i = order.indexOf(hit.size);
      if (pref === 'loose' && i < order.length - 1) i++;
      if (pref === 'fitted' && i > 0) i--;

      sizeEl.textContent = order[i];
      noteEl.textContent = 'Based on ' + h + '" and ' + w + 'lb · ' + pref + ' fit';
      out.classList.add('is-on');

      // Pre-select the recommendation so it takes one tap, not two.
      var input = document.querySelector('[data-v-option][value="' + order[i] + '"]');
      if (input && !input.disabled) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* --- Complete the look ------------------------------------------------ */
  function initLook(root) {
    var look = (root || document).querySelector('[data-v-look]');
    if (!look || look._bound) return;
    look._bound = true;

    look.querySelector('[data-v-look-add]').addEventListener('click', function () {
      var btn = this, label = btn.textContent;
      var items = [];

      var main = document.querySelector('[data-v-variant-id]');
      if (main && main.value) items.push({ id: parseInt(main.value, 10), quantity: 1 });

      look.querySelectorAll('[data-v-look-variant]').forEach(function (s) {
        if (s.value) items.push({ id: parseInt(s.value, 10), quantity: 1 });
      });
      if (!items.length) return;

      btn.disabled = true;
      btn.textContent = 'Adding…';
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          btn.textContent = 'Added to cart';
          return fetch(window.Shopify.routes.root + 'cart.js');
        })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          document.querySelectorAll('[data-v-cart-count]').forEach(function (n) {
            n.textContent = c.item_count;
          });
          setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1200);
        })
        .catch(function () { btn.textContent = label; btn.disabled = false; });
    });
  }


  /* --- Quantity stepper -------------------------------------------------- */
  function initQty(root) {
    (root || document).querySelectorAll('[data-v-qty]').forEach(function (box) {
      if (box._bound) return;
      box._bound = true;
      var input = box.querySelector('[data-v-qty-input]');
      function set(n) {
        input.value = Math.max(1, n);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      box.querySelector('[data-v-qty-up]').addEventListener('click', function () {
        set(parseInt(input.value, 10) + 1);
      });
      box.querySelector('[data-v-qty-down]').addEventListener('click', function () {
        set(parseInt(input.value, 10) - 1);
      });
      input.addEventListener('change', function () {
        if (!input.value || parseInt(input.value, 10) < 1) input.value = 1;
      });
    });
  }

  /* --- Frequently bought together ---------------------------------------
     Every row is pre-checked because opt-out outperforms opt-in, but each is
     genuinely removable so nothing reaches the cart unseen. */
  function initFbt(root) {
    var box = (root || document).querySelector('[data-v-fbt]');
    if (!box || box._bound) return;
    box._bound = true;

    var discount = parseFloat(box.dataset.vDiscount) || 0;
    var totalEl = box.querySelector('[data-v-fbt-total]');
    var compareEl = box.querySelector('[data-v-fbt-compare]');
    var saveEl = box.querySelector('[data-v-fbt-save]');
    var baseEl = box.querySelector('[data-v-fbt-base]');

    function recalc() {
      var sum = parseInt(baseEl.dataset.vFbtBase, 10) || 0;
      box.querySelectorAll('[data-v-fbt-item]').forEach(function (cb) {
        if (!cb.checked) return;
        var sel = cb.closest('.v-fbt__row').querySelector('[data-v-fbt-variant]');
        var price = sel && sel.selectedOptions[0]
          ? parseInt(sel.selectedOptions[0].dataset.price, 10)
          : parseInt(cb.dataset.price, 10);
        sum += price || 0;
      });
      var final = Math.round(sum * (1 - discount / 100));
      totalEl.textContent = money(final);
      if (discount > 0 && saveEl) {
        compareEl.textContent = money(sum);
        compareEl.hidden = false;
        saveEl.textContent = 'Save ' + money(sum - final);
      }
    }

    box.addEventListener('change', recalc);
    recalc();

    box.querySelector('[data-v-fbt-add]').addEventListener('click', function () {
      var btn = this, label = btn.textContent;
      var items = [];

      var main = document.querySelector('[data-v-variant-id]');
      var qtyInput = document.querySelector('[data-v-qty-input]');
      if (main && main.value) {
        items.push({ id: parseInt(main.value, 10), quantity: parseInt(qtyInput ? qtyInput.value : 1, 10) || 1 });
      }

      box.querySelectorAll('[data-v-fbt-item]').forEach(function (cb) {
        if (!cb.checked) return;
        var sel = cb.closest('.v-fbt__row').querySelector('[data-v-fbt-variant]');
        var id = sel ? sel.value : cb.value;
        if (id) items.push({ id: parseInt(id, 10), quantity: 1 });
      });
      if (!items.length) return;

      btn.disabled = true;
      btn.textContent = 'Adding\u2026';
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          btn.textContent = 'Added to cart';
          if (window.VYLNS && window.VYLNS.refreshCart) window.VYLNS.refreshCart(true);
          setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1200);
        })
        .catch(function () { btn.textContent = label; btn.disabled = false; });
    });
  }


  /* --- Quantity stepper -------------------------------------------------- */
  function initQty(root) {
    (root || document).querySelectorAll('[data-v-qty]').forEach(function (btn) {
      if (btn._bound) return;
      btn._bound = true;
      btn.addEventListener('click', function () {
        var input = btn.closest('.v-qty__box').querySelector('[data-v-qty-input]');
        var n = parseInt(input.value, 10) || 1;
        n = btn.dataset.vQty === '+' ? n + 1 : Math.max(1, n - 1);
        input.value = n;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* --- Frequently bought together ---------------------------------------
     Fetched after paint via the Section Rendering API so recommendations
     never block the buy button. */
  function initFbt(root) {
    var host = (root || document).querySelector('[data-v-fbt]');
    if (!host || host._fetched) return;
    host._fetched = true;

    var url = host.dataset.vFbtUrl;
    if (!url) return;

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-v-fbt]');
        if (!fresh || !fresh.querySelector('.v-fbt__row')) { host.remove(); return; }
        host.innerHTML = fresh.innerHTML;
        bindFbt(host);
        if (window.VYLNS && window.VYLNS.reveal) window.VYLNS.reveal(host);
      })
      .catch(function () { host.remove(); });
  }

  function bindFbt(host) {
    var total = host.querySelector('[data-v-fbt-total]');
    var addBtn = host.querySelector('[data-v-fbt-add]');
    if (!addBtn) return;

    var anchorPrice = 0;
    var mainForm = document.querySelector('[data-v-product]');
    if (mainForm) {
      var el = mainForm.querySelector('[data-v-price]');
      if (el) anchorPrice = parseFloat(el.textContent.replace(/[^0-9.]/g, '')) * 100 || 0;
    }

    function recalc() {
      var sum = anchorPrice;
      host.querySelectorAll('.v-fbt__item:not(.is-anchor)').forEach(function (item) {
        var on = item.querySelector('[data-v-fbt-toggle]');
        var sel = item.querySelector('[data-v-fbt-variant]');
        if (on && on.checked && sel) {
          var o = sel.options[sel.selectedIndex];
          if (o) sum += parseInt(o.dataset.price, 10) || 0;
        }
      });
      if (total) total.textContent = money(sum);
    }

    host.querySelectorAll('[data-v-fbt-toggle],[data-v-fbt-variant]').forEach(function (c) {
      c.addEventListener('change', recalc);
    });
    // Checkbox lives inside a <label> wrapping the card — stop the click
    // from toggling twice.
    host.querySelectorAll('[data-v-fbt-variant]').forEach(function (s) {
      s.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); });
    });
    recalc();

    addBtn.addEventListener('click', function () {
      var label = addBtn.textContent;
      var items = [];
      var main = document.querySelector('[data-v-variant-id]');
      var qtyInput = document.querySelector('[data-v-qty-input]');
      if (main && main.value) {
        items.push({ id: parseInt(main.value, 10), quantity: parseInt(qtyInput && qtyInput.value, 10) || 1 });
      }
      host.querySelectorAll('.v-fbt__item:not(.is-anchor)').forEach(function (item) {
        var on = item.querySelector('[data-v-fbt-toggle]');
        var sel = item.querySelector('[data-v-fbt-variant]');
        if (on && on.checked && sel && sel.value) items.push({ id: parseInt(sel.value, 10), quantity: 1 });
      });
      if (!items.length) return;

      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          addBtn.textContent = 'Added to cart';
          document.dispatchEvent(new CustomEvent('vylns:cart:updated', { detail: { open: true } }));
          setTimeout(function () { addBtn.textContent = label; addBtn.disabled = false; }, 1200);
        })
        .catch(function () { addBtn.textContent = label; addBtn.disabled = false; });
    });
  }

  function boot(root) {
    initGallery(root);
    initQty(root);
    initFbt(root);
    initVariants(root);
    initDrawers(root);
    initFitFinder(root);
    initLook(root);
    initQty(root);
    initFbt(root);
  }

  document.addEventListener('DOMContentLoaded', function () { boot(document); });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();

/* Quantity stepper — appended module ---------------------------------------
   Buttons rather than relying on the native number spinner, which is
   effectively untappable on mobile. */
(function () {
  'use strict';
  function init(root) {
    (root || document).querySelectorAll('[data-v-qty]').forEach(function (btn) {
      if (btn._bound) return;
      btn._bound = true;
      btn.addEventListener('click', function () {
        var input = btn.closest('.v-qty__box').querySelector('[data-v-qty-input]');
        var next = (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.vQty, 10);
        input.value = Math.max(1, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
  document.addEventListener('DOMContentLoaded', function () { init(document); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
